import * as THREE from 'three/webgpu';
import { ImportedModelRaycastResolver } from '../../imported-model/selection';
import {
  ImportedModelTopologySelectionService,
  type ImportedModelTopologySelectionInput,
} from '../../imported-model/selection/ImportedModelTopologySelectionService';
import type { EntitySelectionMode } from '../../manager/selection/EntitySelectionMode';
import { isFeatureFace, isFeatureLine, isFeaturePoint } from '../../mesh/feature';
import type { Viewer } from '../../viewer/Viewer';

/**
 * 插入零部件放置吸附命中类型。
 */
export type AssemblyComponentPlacementSnapKind =
  | 'feature-point'
  | 'feature-line'
  | 'feature-face'
  | 'imported-model-surface';

/**
 * 插入零部件放置吸附输入。
 */
export interface AssemblyComponentPlacementSnapInput {
  /** 屏幕 X 坐标。 */
  clientX: number;
  /** 屏幕 Y 坐标。 */
  clientY: number;
  /** 是否允许命中被前景面遮挡的拓扑对象。 */
  allowOccludedSelection?: boolean;
  /** 插入放置使用的拾取模式；默认使用对象模式以启用点/边/面优先仲裁。 */
  selectionMode?: EntitySelectionMode;
}

/**
 * 插入零部件放置吸附结果。
 */
export interface AssemblyComponentPlacementSnapResult {
  /** 吸附后的世界坐标。 */
  position: THREE.Vector3;
  /** 产生吸附的对象。 */
  targetObject: THREE.Object3D;
  /** 吸附命中类型。 */
  kind: AssemblyComponentPlacementSnapKind;
}

/**
 * 插入零部件放置吸附解析器依赖。
 */
export interface AssemblyComponentPlacementSnapResolverOptions {
  /** 当前 Viewer。 */
  viewer: Viewer;
  /** 可选的拓扑选择服务；测试或上层组合可注入该服务以保持解析器职责单一。 */
  topologySelectionService?: Pick<
    ImportedModelTopologySelectionService,
    'collectRaycastTargets' | 'pickAt'
  >;
}

/**
 * 装配体插入零部件放置吸附解析器。
 *
 * 设计说明：
 * 1. 插入预览属于临时交互状态，本解析器只返回预览位置，不创建 mate、不提交历史；
 * 2. 几何命中复用 imported-model 拓扑拾取链，避免 web-client 自行实现点/边/面射线规则；
 * 3. 默认使用 object 模式，让已有拓扑仲裁逻辑在点、边、面之间选择最符合当前指针的目标。
 */
export class AssemblyComponentPlacementSnapResolver {
  /** 仅记录内部创建的服务；外部注入服务的销毁责任仍属于调用方。 */
  private readonly ownedTopologySelectionService: ImportedModelTopologySelectionService | null;
  /** imported-model 拓扑选择服务。 */
  private readonly topologySelectionService: Pick<
    ImportedModelTopologySelectionService,
    'collectRaycastTargets' | 'pickAt'
  >;

  /**
   * 构造函数。
   *
   * @param options 解析器依赖。
   */
  public constructor(options: AssemblyComponentPlacementSnapResolverOptions) {
    this.ownedTopologySelectionService = options.topologySelectionService
      ? null
      : new ImportedModelTopologySelectionService({
        renderer: options.viewer,
        importedModelRaycastResolver: new ImportedModelRaycastResolver(options.viewer),
      });
    // 显式区分拥有与借用，不能仅检查服务上有没有 dispose 就越权销毁外部依赖。
    this.topologySelectionService = options.topologySelectionService ?? this.ownedTopologySelectionService!;
  }

  /** 结束自身创建的拾取服务持有期，不销毁注入服务或外部模型。 */
  public dispose(): void {
    this.ownedTopologySelectionService?.dispose();
  }

  /**
   * 解析当前指针位置对应的插入放置吸附点。
   *
   * @param input 放置吸附输入。
   * @returns 吸附结果；未命中模型几何时返回 `null`。
   */
  public resolve(input: AssemblyComponentPlacementSnapInput): AssemblyComponentPlacementSnapResult | null {
    const interactableObjects = this.topologySelectionService.collectRaycastTargets();
    if (interactableObjects.length === 0) {
      return null;
    }

    const hit = this.topologySelectionService.pickAt({
      clientX: input.clientX,
      clientY: input.clientY,
      interactableObjects,
      selectionMode: input.selectionMode ?? 'object',
      allowOccludedSelection: input.allowOccludedSelection ?? false,
    } satisfies ImportedModelTopologySelectionInput);
    if (!hit || !this.isFinitePoint(hit.point)) {
      return null;
    }

    return this.resolveHitSnapResult(hit);
  }

  /**
   * 将拓扑拾取命中转换为插入放置吸附结果。
   *
   * @param hit imported-model 拾取命中。
   * @returns 吸附结果；命中坐标无效时返回 `null`。
   */
  private resolveHitSnapResult(
    hit: THREE.Intersection<THREE.Object3D>,
  ): AssemblyComponentPlacementSnapResult | null {
    if (isFeaturePoint(hit.object)) {
      const point = hit.object.getWorldPoint();
      return this.createSnapResult(point, hit.object, 'feature-point');
    }

    if (isFeatureLine(hit.object)) {
      return this.createSnapResult(hit.point, hit.object, 'feature-line');
    }

    if (isFeatureFace(hit.object)) {
      return this.createSnapResult(hit.point, hit.object, 'feature-face');
    }

    return this.createSnapResult(hit.point, hit.object, 'imported-model-surface');
  }

  /**
   * 创建标准吸附结果并隔离内部向量引用。
   *
   * @param position 世界坐标。
   * @param targetObject 吸附对象。
   * @param kind 吸附类型。
   * @returns 吸附结果；坐标非法时返回 `null`。
   */
  private createSnapResult(
    position: THREE.Vector3,
    targetObject: THREE.Object3D,
    kind: AssemblyComponentPlacementSnapKind,
  ): AssemblyComponentPlacementSnapResult | null {
    if (!this.isFinitePoint(position)) {
      return null;
    }

    return {
      position: position.clone(),
      targetObject,
      kind,
    };
  }

  /**
   * 判断坐标是否可用于放置。
   *
   * @param point 待检查坐标。
   * @returns 坐标是否全部为有限数值。
   */
  private isFinitePoint(point: THREE.Vector3): boolean {
    return Number.isFinite(point.x)
      && Number.isFinite(point.y)
      && Number.isFinite(point.z);
  }
}
